import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeUp, stagger } from "./motion";
import { MARKETS } from "../data/species";
import { useLang } from "../i18n";

const ORIGIN = MARKETS.find((m) => m.origin) || MARKETS[0];

const routePath = (m) => {
  const x1 = ORIGIN.x, y1 = ORIGIN.y, x2 = m.x, y2 = m.y;
  const cx = (x1 + x2) / 2;
  const cy = Math.min(y1, y2) - 16;
  return `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
};

export const ExportMap = () => {
  const [active, setActive] = useState(null);
  const { t } = useLang();

  const activeMarket = active != null ? MARKETS[active] : null;
  const showRoute = activeMarket && !activeMarket.origin;

  return (
    <section
      id="markets"
      data-testid="export-map"
      className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-40"
    >
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="mb-14 flex flex-col justify-between gap-8 md:flex-row md:items-end"
      >
        <div>
          <motion.span variants={fadeUp} className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">
            {t("map.kicker")}
          </motion.span>
          <motion.h2 variants={fadeUp} className="mt-4 max-w-2xl font-serif text-5xl font-medium leading-[0.95] tracking-tight text-brand-blue md:text-6xl">
            {t("map.title")}
          </motion.h2>
        </div>
        <motion.p variants={fadeUp} className="max-w-sm font-light text-base leading-relaxed text-brand-slate">
          {t("map.sub")}
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        {/* Map */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative lg:col-span-8"
        >
          <div className="relative w-full overflow-hidden rounded-sm border border-border bg-brand-blue">
            <img src="/world-map.jpg" alt="World map" className="w-full opacity-90 mix-blend-screen" />

            {/* Animated shipping route */}
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              data-testid="route-overlay"
            >
              <AnimatePresence>
                {showRoute && (
                  <g key={active}>
                    <motion.path
                      d={routePath(activeMarket)}
                      fill="none"
                      stroke="#8B5A2B"
                      strokeWidth={1.4}
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 0.9 }}
                      exit={{ opacity: 0 }}
                      transition={{ pathLength: { duration: 0.9, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 0.25 } }}
                    />
                    <motion.path
                      d={routePath(activeMarket)}
                      fill="none"
                      stroke="#F7F5F0"
                      strokeWidth={1}
                      strokeDasharray="2 3"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                      className="route-flow"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0.85 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                    />
                  </g>
                )}
              </AnimatePresence>
            </svg>

            {MARKETS.map((m, i) => {
              const isActive = active === i;
              return (
                <button
                  key={m.port}
                  data-testid={`map-marker-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${m.x}%`, top: `${m.y}%` }}
                >
                  <span className="relative flex h-3 w-3 items-center justify-center">
                    <span
                      className={`absolute inline-flex h-full w-full rounded-full ${m.origin ? "bg-brand-timber" : "bg-brand-bone"} ${isActive ? "opacity-80" : "opacity-60"} animate-ping`}
                    />
                    <span
                      className={`relative inline-flex rounded-full ${m.origin ? "h-3 w-3 bg-brand-timber ring-2 ring-brand-bone" : "h-2.5 w-2.5 bg-brand-bone"} transition-transform duration-300 ${isActive ? "scale-150" : ""}`}
                    />
                  </span>
                  {isActive && (
                    <motion.span
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute left-1/2 top-4 z-10 -translate-x-1/2 whitespace-nowrap rounded-sm bg-brand-bone px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-brand-blue shadow-lg"
                    >
                      {m.port}
                    </motion.span>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Market list */}
        <motion.ul
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="lg:col-span-4"
        >
          {MARKETS.map((m, i) => (
            <motion.li
              key={m.port}
              variants={fadeUp}
              data-testid={`market-item-${i}`}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              className={`flex items-center justify-between border-b border-border py-4 transition-colors duration-300 ${active === i ? "text-brand-timber" : "text-brand-blue"}`}
            >
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-brand-slate">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-2xl md:text-3xl">{m.region}</span>
              </div>
              <span className="font-mono text-[11px] uppercase tracking-wider text-brand-slate">{m.port}</span>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};
